import React, { useState } from 'react';
import {
    Box, Typography, TextField, Button,
    IconButton, InputAdornment, Alert, CircularProgress
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import { apiFetch } from '../api';

/**
 * AdminLoginPage — Hidden Super Admin login.
 * Route: /csmadmin
 *
 * This page is NOT linked from anywhere in the public UI.
 * Only the developer knows this URL.
 */
const AdminLoginPage = () => {
    const [email, setEmail]           = useState('');
    const [password, setPassword]     = useState('');
    const [showPassword, setShowPwd]  = useState(false);
    const [error, setError]           = useState('');
    const [loading, setLoading]       = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Use /login — this hits GlobalAuthController which handles SuperAdmin
            const res = await apiFetch('/login', {
                method:      'POST',
                body:        JSON.stringify({ email, password }),
                credentials: 'include', // Overwrite omit if necessary
            });
            
            // apiFetch returns the raw response object, so we must parse it
            const data = await res.json();

            if (res.ok) {
                // Store minimal info in localStorage for ProtectedRoute check
                localStorage.setItem('csm_user',  JSON.stringify(data.user));
                localStorage.setItem('csm_role',  'super_admin');
                window.location.href = '/admin';
            } else {
                setError(data.message || 'Credentials चुकीचे आहेत.');
            }
        } catch {
            setError('Server शी संपर्क होऊ शकला नाही.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight:       '100vh',
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            background:      '#0D0D0D',
        }}>
            <Box
                component="form"
                onSubmit={handleLogin}
                sx={{
                    width:         360,
                    p:             4,
                    borderRadius:  3,
                    bgcolor:       '#1A1A1A',
                    border:        '1px solid #2a2a2a',
                    boxShadow:     '0 20px 60px rgba(0,0,0,0.6)',
                }}
            >
                {/* Minimal header — no branding */}
                <Typography
                    variant="h6"
                    sx={{ mb: 3, color: '#555', fontFamily: 'monospace', letterSpacing: 4, textAlign: 'center' }}
                >
                    ▣ &nbsp; SYSTEM ACCESS
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2, bgcolor: '#2a0a0a', color: '#ff6b6b', border: '1px solid #5c1a1a' }}>
                        {error}
                    </Alert>
                )}

                <TextField
                    fullWidth required
                    label="Email"
                    variant="outlined"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="off"
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            color:            '#ccc',
                            '& fieldset':     { borderColor: '#333' },
                            '&:hover fieldset': { borderColor: '#555' },
                            '&.Mui-focused fieldset': { borderColor: '#0FA958' },
                        },
                        '& .MuiInputLabel-root': { color: '#555' },
                        '& .MuiInputLabel-root.Mui-focused': { color: '#0FA958' },
                    }}
                />

                <TextField
                    fullWidth required
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="off"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPwd(v => !v)}
                                    sx={{ color: '#555' }}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            color:            '#ccc',
                            '& fieldset':     { borderColor: '#333' },
                            '&:hover fieldset': { borderColor: '#555' },
                            '&.Mui-focused fieldset': { borderColor: '#0FA958' },
                        },
                        '& .MuiInputLabel-root': { color: '#555' },
                        '& .MuiInputLabel-root.Mui-focused': { color: '#0FA958' },
                    }}
                />

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
                    sx={{
                        py:      1.5,
                        bgcolor: '#0FA958',
                        color:   '#000',
                        fontWeight: 'bold',
                        '&:hover':   { bgcolor: '#0C8A46' },
                        '&.Mui-disabled': { bgcolor: '#1a3a2a', color: '#2a6a4a' },
                    }}
                >
                    {loading ? 'Verifying...' : 'Access'}
                </Button>
            </Box>
        </Box>
    );
};

export default AdminLoginPage;
